import React, { useState, useEffect, useMemo } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Calendar as CalendarIcon, Trash, FloppyDisk, Link as LinkIcon, Copy, Upload, Plus, Edit, UserPlus } from 'iconoir-react';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import DeviceSessionsCard from '../components/settings/DeviceSessionsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCIES = ['USD', 'INR', 'GBP', 'CAD', 'EUR', 'AUD', 'PKR', 'BDT', 'LKR', 'NPR'];

function SaveTemplateForm({ weddingId, sourceTemplateIds, allTemplates }) {
  const isFusion = (sourceTemplateIds || []).length >= 2;
  const fusionLabel = useMemo(() => {
    if (!isFusion) return '';
    const lookup = new Map((allTemplates || []).map((t) => [t.id, t.name]));
    return sourceTemplateIds
      .map((id) => lookup.get(id) || id)
      .join(' + ');
  }, [isFusion, sourceTemplateIds, allTemplates]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Pre-fill a sensible default name once we know this wedding is a fusion,
  // so brides can save it in one click instead of typing the combo by hand.
  useEffect(() => {
    if (isFusion && !name) {
      setName(`My ${fusionLabel} Fusion`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFusion, fusionLabel]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name || !weddingId) return;
    setSaving(true);
    try {
      await axios.post(`${API}/templates/save-custom`, {
        wedding_id: weddingId,
        name,
        description
      }, { withCredentials: true });
      toast.success('Template saved! You can now use it when creating new weddings.');
      setName('');
      setDescription('');
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-4">
      {isFusion && (
        <div
          className="flex items-start gap-2 rounded-sm border border-terracotta/30 bg-terracotta/5 px-3 py-2"
          data-testid="fusion-source-banner"
        >
          <span className="font-mono text-xs uppercase tracking-wider text-terracotta">
            Fusion wedding
          </span>
          <span className="text-xs text-muted">
            Built from {sourceTemplateIds.length} templates: <span className="text-charcoal">{fusionLabel}</span>. Save it as a custom template to reuse this exact combination later.
          </span>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Template Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="input-field h-10"
            placeholder="e.g., My Punjabi Wedding Template" required data-testid="template-name-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Description</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} className="input-field h-10"
            placeholder="Optional description" data-testid="template-description-input" />
        </div>
      </div>
      <Button type="submit" disabled={saving || !name} className="btn-primary text-xs" data-testid="save-template-btn">
        {saving ? 'Saving...' : 'Save as Template'}
      </Button>
    </form>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const { currentWedding, updateWedding, deleteWedding, currencies, fetchWeddings, templates, fetchTemplates } = useWedding();
  
  const [weddingForm, setWeddingForm] = useState({
    name: '',
    wedding_date: '',
    total_budget: '',
    preferred_currency: 'USD',
    partner1_name: '',
    partner1_role: '',
    partner2_name: '',
    partner2_role: '',
    planning_scope: 'both',
  });
  const [saving, setSaving] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'view' });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Pre-load the built-in templates list so the fusion-source banner can
  // map source_template_ids → human-readable names instead of bare IDs.
  useEffect(() => {
    if (!templates || templates.length === 0) fetchTemplates();
  }, [templates, fetchTemplates]);

  useEffect(() => {
    if (!currentWedding) return;
    setWeddingForm({
      name: currentWedding.name || '',
      wedding_date: currentWedding.wedding_date || '',
      total_budget: currentWedding.total_budget?.toString() || '',
      preferred_currency: currentWedding.preferred_currency || 'USD',
      partner1_name: currentWedding.partner1_name || '',
      partner1_role: currentWedding.partner1_role || '',
      partner2_name: currentWedding.partner2_name || '',
      partner2_role: currentWedding.partner2_role || '',
      planning_scope: currentWedding.planning_scope || 'both',
    });
    // Inline collaborator fetch — keeps the effect self-contained and lets
    // ESLint's exhaustive-deps reason about `currentWedding.id` only.
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/collaborators/${currentWedding.id}`,
          { withCredentials: true },
        );
        setCollaborators(data);
      } catch (err) {
        console.error('Failed to fetch collaborators:', err);
      }
    })();
  }, [currentWedding]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCollaborators = async () => {
    if (!currentWedding) return;
    try {
      const { data } = await axios.get(`${API}/collaborators/${currentWedding.id}`, { withCredentials: true });
      setCollaborators(data);
    } catch (err) { console.error('Failed to fetch collaborators:', err); }
  };

  const handleInviteCollaborator = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/collaborators`, {
        wedding_id: currentWedding.id,
        email: inviteForm.email,
        name: inviteForm.name,
        role: inviteForm.role
      }, { withCredentials: true });
      toast.success('Collaborator invited!');
      setInviteForm({ email: '', name: '', role: 'view' });
      setShowInviteForm(false);
      fetchCollaborators();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    }
  };

  const handleRemoveCollaborator = async (collabId) => {
    try {
      await axios.delete(`${API}/collaborators/${collabId}`, { withCredentials: true });
      toast.success('Collaborator removed');
      fetchCollaborators();
    } catch (err) { toast.error('Failed to remove'); }
  };

  const handleUpdateCollabRole = async (collabId, newRole) => {
    try {
      await axios.put(`${API}/collaborators/${collabId}`, { role: newRole }, { withCredentials: true });
      fetchCollaborators();
    } catch (err) { toast.error('Failed to update role'); }
  };

  const handlePhotoUpload = async (e, photoType) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload/photo?photo_type=${photoType}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await axios.put(`${API}/weddings/${currentWedding.id}/photos`, {
        [photoType === 'profile' ? 'profile_photo' : 'cover_photo']: data.path
      }, { withCredentials: true });
      toast.success(`${photoType === 'profile' ? 'Profile' : 'Cover'} photo updated!`);
      fetchWeddings();
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSaveWedding = async (e) => {
    e.preventDefault();
    if (!currentWedding) return;
    
    setSaving(true);
    try {
      await updateWedding(currentWedding.id, {
        name: weddingForm.name,
        wedding_date: weddingForm.wedding_date || null,
        total_budget: parseFloat(weddingForm.total_budget) || 0,
        preferred_currency: weddingForm.preferred_currency,
        partner1_name: weddingForm.partner1_name || null,
        partner1_role: weddingForm.partner1_role || null,
        partner2_name: weddingForm.partner2_name || null,
        partner2_role: weddingForm.partner2_role || null,
        planning_scope: weddingForm.planning_scope || 'both',
      });
      toast.success('Wedding settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWedding = async () => {
    if (!currentWedding) return;
    try {
      await deleteWedding(currentWedding.id);
      toast.success('Wedding deleted');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete wedding');
    }
  };

  if (!currentWedding) {
    return (
      <Layout>
        <div className="text-center py-12 text-muted">
          Please select or create a wedding first.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 max-w-2xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight">
            Settings
          </h1>
          <p className="text-muted font-light mt-2">
            Manage your wedding and account settings
          </p>
        </div>

        {/* Account Info */}
        <Card className="card-surface p-6">
          <h2 className="font-serif text-xl text-charcoal mb-4">Account</h2>
          <div className="space-y-3">
            <div>
              <Label className="text-muted text-sm">Name</Label>
              <p className="text-charcoal">{user?.name}</p>
            </div>
            <div>
              <Label className="text-muted text-sm">Email</Label>
              <p className="text-charcoal">{user?.email}</p>
            </div>
          </div>
        </Card>

        {/* Wedding Settings */}
        <Card className="card-surface p-6">
          <h2 className="font-serif text-xl text-charcoal mb-6">Wedding Settings</h2>
          <form onSubmit={handleSaveWedding} className="space-y-5">
            {/* Planning scope toggle — switches whether Partner 2 is involved
                across the app. Pure UI/preference field; no destructive
                migration is needed since we keep all partner_2 data on
                disk regardless of mode. */}
            <div className="space-y-2 pb-4 border-b border-border" data-testid="settings-scope-section">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Planning Scope</Label>
              <p className="text-[11px] text-muted leading-relaxed">
                Are you planning the events for both families, or just your side?
                You can switch anytime.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                {[
                  { value: 'both', title: "Both families' events", desc: "Both partners plus any joint celebrations." },
                  { value: 'self', title: 'Just my events', desc: 'Only the events on your side.' },
                ].map((opt) => {
                  const selected = weddingForm.planning_scope === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setWeddingForm({ ...weddingForm, planning_scope: opt.value })}
                      data-testid={`settings-scope-option-${opt.value}`}
                      className={`text-left p-3 rounded-sm border-2 transition-all ${
                        selected
                          ? 'border-terracotta bg-terracotta/5'
                          : 'border-border bg-white hover:border-charcoal/40'
                      }`}
                    >
                      <p className={`text-sm font-medium ${selected ? 'text-terracotta' : 'text-charcoal'}`}>
                        {opt.title}
                      </p>
                      <p className="text-[11px] text-muted mt-0.5 leading-relaxed">{opt.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3 pb-2 border-b border-border" data-testid="settings-couple-section">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">The Couple</Label>
              {[1, 2].map((idx) => (
                <div key={idx} className="grid grid-cols-[1fr_140px] gap-3">
                  <Input
                    value={weddingForm[`partner${idx}_name`]}
                    onChange={(e) => setWeddingForm({ ...weddingForm, [`partner${idx}_name`]: e.target.value })}
                    className="input-field h-10"
                    placeholder={`Partner ${idx} name`}
                    data-testid={`settings-partner${idx}-name`}
                  />
                  <Select
                    value={weddingForm[`partner${idx}_role`]}
                    onValueChange={(v) => setWeddingForm({ ...weddingForm, [`partner${idx}_role`]: v })}
                  >
                    <SelectTrigger className="input-field h-10" data-testid={`settings-partner${idx}-role`}>
                      <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bride">Bride</SelectItem>
                      <SelectItem value="groom">Groom</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Wedding Name</Label>
              <Input
                value={weddingForm.name}
                onChange={(e) => setWeddingForm({ ...weddingForm, name: e.target.value })}
                className="input-field h-12"
                placeholder="Wedding name"
                required
                data-testid="settings-wedding-name"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Wedding Date</Label>
              <Input
                type="date"
                value={weddingForm.wedding_date}
                onChange={(e) => setWeddingForm({ ...weddingForm, wedding_date: e.target.value })}
                className="input-field h-12"
                data-testid="settings-wedding-date"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Total Budget</Label>
                <Input
                  type="number"
                  value={weddingForm.total_budget}
                  onChange={(e) => setWeddingForm({ ...weddingForm, total_budget: e.target.value })}
                  className="input-field h-12 font-mono"
                  placeholder="0"
                  min="0"
                  step="0.01"
                  data-testid="settings-wedding-budget"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Currency</Label>
                <Select 
                  value={weddingForm.preferred_currency} 
                  onValueChange={(value) => setWeddingForm({ ...weddingForm, preferred_currency: value })}
                >
                  <SelectTrigger className="input-field h-12" data-testid="settings-wedding-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((currency) => (
                      <SelectItem key={currency} value={currency}>{currency}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="btn-primary flex items-center gap-2"
              data-testid="save-settings-btn"
            >
              <FloppyDisk className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </form>
        </Card>

        {/* Photos */}
        <Card className="card-surface p-6">
          <h2 className="font-serif text-xl text-charcoal mb-4">Photos</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm text-muted uppercase tracking-wider mb-2 block">Profile Photo</Label>
              {currentWedding?.profile_photo ? (
                <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-border mb-3">
                  <img src={`${API}/files/${currentWedding.profile_photo}`} alt="Profile" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-full bg-page border-2 border-dashed border-border flex items-center justify-center mb-3">
                  <Upload className="w-6 h-6 text-muted" />
                </div>
              )}
              <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer text-xs">
                <Upload className="w-3 h-3" />
                {uploadingPhoto ? 'Uploading...' : 'Upload Profile Photo'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'profile')} disabled={uploadingPhoto} />
              </label>
            </div>
            <div>
              <Label className="text-sm text-muted uppercase tracking-wider mb-2 block">Cover Photo</Label>
              {currentWedding?.cover_photo ? (
                <div className="w-full h-24 rounded-sm overflow-hidden border border-border mb-3">
                  <img src={`${API}/files/${currentWedding.cover_photo}`} alt="Cover" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-full h-24 rounded-sm bg-page border-2 border-dashed border-border flex items-center justify-center mb-3">
                  <Upload className="w-6 h-6 text-muted" />
                </div>
              )}
              <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer text-xs">
                <Upload className="w-3 h-3" />
                {uploadingPhoto ? 'Uploading...' : 'Upload Cover Photo'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoUpload(e, 'cover')} disabled={uploadingPhoto} />
              </label>
            </div>
          </div>
        </Card>

        {/* Devices & Sessions — security panel */}
        <DeviceSessionsCard />

        {/* Collaborative Planning */}
        <Card className="card-surface p-6" data-testid="collaborators-card">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="font-serif text-xl text-charcoal">Collaborators</h2>
              <p className="text-xs text-muted mt-1" data-testid="collaborators-count">
                {collaborators.length} of 3 {collaborators.length === 1 ? 'person' : 'people'} added
              </p>
            </div>
            <Button
              onClick={() => setShowInviteForm(!showInviteForm)}
              disabled={collaborators.length >= 3}
              className="btn-secondary flex items-center gap-2 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="invite-collaborator-btn"
            >
              <UserPlus className="w-4 h-4" /> Invite
            </Button>
          </div>
          <p className="text-sm text-muted mb-4">
            Invite your partner, planner, or parents to collaborate — up to 3 people.
            Editors can add and change everything; viewers can only look.
          </p>

          {collaborators.length >= 3 && !showInviteForm && (
            <p className="text-xs text-terracotta mb-4" data-testid="collaborators-limit-message">
              You've reached the 3-person limit. Remove someone to add another.
            </p>
          )}

          {showInviteForm && (
            <form onSubmit={handleInviteCollaborator} className="p-4 bg-page rounded-sm mb-4 space-y-3" data-testid="invite-collaborator-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="input-field h-10" placeholder="Email address" type="email" required data-testid="invite-email-input" />
                <Input value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  className="input-field h-10" placeholder="Name (optional)" data-testid="invite-name-input" />
                <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                  <SelectTrigger className="input-field h-10" data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="edit">Can Edit</SelectItem>
                    <SelectItem value="view">View Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="btn-primary text-xs" data-testid="send-invite-btn">Send Invite</Button>
            </form>
          )}

          {collaborators.length === 0 ? (
            <p className="text-muted text-sm py-2" data-testid="collaborators-empty">No collaborators yet. Invite someone to get started!</p>
          ) : (
            <div className="space-y-2">
              {collaborators.map((collab) => (
                <div key={collab.id} className="flex items-center justify-between p-3 bg-page rounded-sm" data-testid={`collaborator-row-${collab.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-charcoal text-sm truncate">{collab.name || collab.email}</span>
                      {collab.status === 'pending' && (
                        <Badge className="bg-ochre/10 text-ochre border text-[10px] uppercase tracking-wider" data-testid={`collaborator-pending-badge-${collab.id}`}>
                          Pending
                        </Badge>
                      )}
                    </div>
                    {collab.name && <span className="text-muted text-xs">{collab.email}</span>}
                    {collab.status === 'pending' && (
                      <p className="text-[11px] text-muted mt-0.5">Invite email sent — waiting for them to register.</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Select value={collab.role} onValueChange={(v) => handleUpdateCollabRole(collab.id, v)}>
                      <SelectTrigger className="w-28 h-8 text-xs" data-testid={`collaborator-role-select-${collab.id}`}>
                        <Badge className={`${collab.role === 'edit' ? 'bg-moss/10 text-moss' : 'bg-ochre/10 text-ochre'} border text-xs`}>
                          {collab.role === 'edit' ? 'Can Edit' : 'View Only'}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="edit">Can Edit</SelectItem>
                        <SelectItem value="view">View Only</SelectItem>
                      </SelectContent>
                    </Select>
                    <button onClick={() => handleRemoveCollaborator(collab.id)} className="p-1.5 text-muted hover:text-terracotta transition-colors" data-testid={`remove-collaborator-btn-${collab.id}`}>
                      <Trash className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Currency Rates */}
        <Card className="card-surface p-6">
          <h2 className="font-serif text-xl text-charcoal mb-4">Currency Exchange Rates</h2>
          <p className="text-sm text-muted mb-4">
            {currencies.live
              ? 'Live exchange rates (updated hourly) relative to USD'
              : 'Exchange rates relative to USD (fallback static rates)'}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {currencies.currencies?.map((currency) => (
              <div key={currency} className="flex justify-between p-3 bg-page rounded-sm">
                <span className="text-charcoal">{currency}</span>
                <span className="font-mono text-muted">{currencies.rates?.[currency]?.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Save as Template */}
        <Card className="card-surface p-6">
          <h2 className="font-serif text-xl text-charcoal mb-2">Save as Template</h2>
          <p className="text-sm text-muted mb-4">
            Save your current wedding's events and checklist as a reusable template for future weddings.
          </p>
          <SaveTemplateForm
            weddingId={currentWedding?.id}
            sourceTemplateIds={currentWedding?.source_template_ids}
            allTemplates={templates}
          />
        </Card>

        {/* Danger Zone — locked for paid users (lifetime purchase is tied to this wedding).
            Collaborators can't delete either (it isn't their wedding). */}
        {user?.is_paid && user?.role !== 'admin' ? (
          <Card className="card-surface p-6 border-moss/30" data-testid="wedding-locked-card">
            <h2 className="font-serif text-xl text-moss mb-3">Wedding Locked</h2>
            <p className="text-sm text-muted mb-2">
              Your lifetime purchase is tied to this wedding, so it can't be deleted —
              that's how we keep the one-time price fair for everyone.
            </p>
            <p className="text-sm text-muted">
              Need to start over? Email us at{' '}
              <a href="mailto:hello@shaadiplanner.app" className="text-terracotta hover:underline">hello@shaadiplanner.app</a>
              {' '}and we'll help you out.
            </p>
          </Card>
        ) : user?.is_collaborator && !user?.is_paid ? null : (
          <Card className="card-surface p-6 border-terracotta/30">
            <h2 className="font-serif text-xl text-terracotta mb-4">Danger Zone</h2>
            <p className="text-sm text-muted mb-4">
              Once you delete a wedding, there is no going back. All events, expenses, vendors, guests, and checklist items will be permanently deleted.
            </p>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="bg-terracotta/10 text-terracotta hover:bg-terracotta hover:text-white border border-terracotta/30 flex items-center gap-2"
                  data-testid="delete-wedding-btn"
                >
                  <Trash className="w-4 h-4" />
                  Delete Wedding
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-serif">Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete your wedding
                    "{currentWedding?.name}" and all its associated data including events, expenses,
                    vendors, guests, and checklist items.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteWedding}
                    className="bg-terracotta text-white hover:bg-terracotta-dark"
                    data-testid="confirm-delete-wedding-btn"
                  >
                    Delete Wedding
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </Card>
        )}
      </div>
    </Layout>
  );
}
